import React, { useTransition, useState, useEffect } from "react";
import {
  Table,
  Button,
  Select,
  Space,
  Input,
  InputNumber,
  DatePicker,
  Switch,
  Slider,
  Radio,
  Checkbox,
  Popconfirm,
} from "antd";
import dayjs from "dayjs";

const { Option } = Select;

// ─────────────── TextEditableCell ───────────────
interface TextEditableCellProps {
  value: string;
  onCommit: (newValue: string) => void;
  width?: number;
}
const TextEditableCell: React.FC<TextEditableCellProps> = ({
  value,
  onCommit,
  width,
}) => {
  const [local, setLocal] = useState(value);
  useEffect(() => {
    setLocal(value);
  }, [value]);
  return (
    <Input
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => onCommit(local)}
      style={{ width: width }}
    />
  );
};

// ─────────────── AddingTable ───────────────
export interface EditableColumnType<T> {
  title: string;
  dataIndex: string;
  width?: number;
  editable?: boolean;
  inputType?:
    | "text"
    | "number"
    | "select"
    | "date"
    | "checkbox"
    | "radio"
    | "switch"
    | "slider";
  options?: Array<{ value: string | number; label: string }>;
}

const AddingTable = <T extends { [k: string]: any }>({
  columns,
  data,
  setData,
  rowKeyField = "RowNo",
}: any) => {
  const [isPending, startTransition] = useTransition();
  const getKey = (rec: T) => String(rec[rowKeyField] ?? "");

  const updateSync = (key: string, idx: string, val: any) =>
    setData((prev) =>
      prev.map((item) =>
        getKey(item) === key ? { ...item, [idx]: val } : item
      )
    );

  const updateAsync = (key: string, idx: string, val: any) =>
    startTransition(() => {
      setData((prev) =>
        prev.map((item) =>
          getKey(item) === key ? { ...item, [idx]: val } : item
        )
      );
    });

  const addNewRow = () => {
    const newRow: any = { [rowKeyField]: data.length + 1 };
    columns.forEach((col) => {
      if (col.dataIndex && newRow[col.dataIndex] === undefined) {
        newRow[col.dataIndex] = "";
      }
    });
    setData([...data, newRow]);
  };

  const merged = columns.map((col) => {
    if (!col.editable) return col;
    return {
      ...col,
      render: (text: any, record: T) => {
        const key = getKey(record);
        // text inputs via separate component
        if (col.inputType === "text") {
          return (
            <TextEditableCell
              value={text}
              onCommit={(v) => updateSync(key, col.dataIndex, v)}
              width={col.width ?? 150}
            />
          );
        }
        // other inputs
        const common = {
          value: record[col.dataIndex],
          onChange: (e: any) => {
            const v = e && e.target ? e.target.value : e;
            updateAsync(key, col.dataIndex, v);
          },
        };
        switch (col.inputType) {
          case "number":
            return <InputNumber {...common} />;
          case "select":
            let opts: { value: any; label: string }[] = [];
            if (typeof col.options === "function") {
              opts = col.options(record);
            } else if (Array.isArray(col.options)) {
              opts = col.options;
            }
            return (
              <Select
                {...common}
                showSearch
                style={{ width: "100%", minWidth: 200 }}
                optionFilterProp="children"
              >
                {opts?.map((o) => (
                  <Option key={o.value} value={o.value}>
                    {o.label}
                  </Option>
                ))}
              </Select>
            );
          case "date":
            return (
              <DatePicker
                value={
                  record[col.dataIndex] ? dayjs(record[col.dataIndex]) : null
                }
                onChange={(_, ds) => updateAsync(key, col.dataIndex, ds)}
              />
            );
          case "checkbox":
            return (
              <Checkbox
                checked={record[col.dataIndex]}
                onChange={(e) =>
                  updateAsync(key, col.dataIndex, e.target.checked)
                }
              />
            );
          case "radio":
            return (
              <Radio.Group
                options={col.options}
                value={record[col.dataIndex]}
                onChange={(e) =>
                  updateAsync(key, col.dataIndex, e.target.value)
                }
              />
            );
          case "switch":
            return (
              <Switch
                checked={record[col.dataIndex]}
                onChange={(c) => updateAsync(key, col.dataIndex, c)}
              />
            );
          case "slider":
            return (
              <Slider
                value={record[col.dataIndex]}
                onChange={(v) => updateAsync(key, col.dataIndex, v)}
              />
            );
          default:
            return <Input {...common} />;
        }
      },
    };
  });

  const finalColumns = [
    ...merged,
    {
      title: "الإجراءات",
      dataIndex: "operation",
      render: (_: any, record: T) => (
        <Popconfirm
          title="هل أنت متأكد؟"
          onConfirm={() =>
            setData(data.filter((i) => getKey(i) !== getKey(record)))
          }
        >
          <Button danger>Delete</Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <>
      <div className="mb-4">
        <Button onClick={addNewRow} type="primary">
          إضافة
        </Button>
      </div>
      <Table
        // scroll={{ x: 1200 }}
        bordered
        dataSource={data}
        columns={finalColumns}
        rowKey={rowKeyField}
      />
    </>
  );
};

export default AddingTable;
